// F3c-c1 GREEN — WaiverPanel. Inline player-waiver editor, ported from
// deprecated-business@16fd6b71.
//
//   - A textarea PRE-FILLED with DEFAULT_PLAYER_WAIVER (the 3-paragraph liability
//     / assumption-of-risk default) when no waiver is published, or with the
//     existing waiver content when one exists.
//   - GET /facilities/{id}/waiver (getFacilityWaiver) loads the current version;
//     POST (publishFacilityWaiver) publishes the textarea body as a new version.
//   - Once a version exists it shows a "Published" status row with the exact
//     "Version N published on …" sentence, and the CTA reads "Publish New
//     Version". (fix/golive-anytime: light visual polish only — the status
//     row is now a Badge + the same sentence in a neutral container instead of
//     a green "success" banner; no publish/versioning logic changed.)
//   - With no facility yet it tells the operator to create the facility first and
//     disables publishing (the waiver read is NOT issued without a facility id).
//   - The service tags this waiver applies to (e.g. "Reservations", "Open
//     Play") are already rendered as Badges by the parent ReviewStep wrapper
//     (getting-started.tsx renders step.applies_to_services) — NOT duplicated
//     here, so this panel never re-derives that backend-owned service set.
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  getFacilityWaiver,
  publishFacilityWaiver,
  type FacilityDetail,
} from "@/libs/facilityApi";
import { FormMessage } from "./shared";

// The default liability / assumption-of-risk waiver players accept. Three
// paragraphs separated by blank lines (\n\n): (1) inherent-risk acknowledgement +
// rules, (2) fitness certification + release/hold-harmless, (3) emergency care +
// staff-instruction compliance + removal authority.
export const DEFAULT_PLAYER_WAIVER = `By participating in activities at this facility, I acknowledge that badminton and related physical activity involve inherent risks, including falls, collisions, equipment-related injuries, and other unforeseen hazards. I voluntarily assume these risks and agree to follow all posted facility rules, staff instructions, and court etiquette requirements.

I certify that I am physically able to participate. I agree to immediately stop playing and notify staff if I feel unsafe, unwell, or observe unsafe conditions. I release and hold harmless the facility, its owners, staff, coaches, and affiliates from claims arising from ordinary negligence associated with my participation, except where prohibited by law.

I understand that this facility may use emergency medical assistance when necessary and that I remain responsible for my own medical costs. I agree to follow court assignment, queue, rotation, and drop-in instructions from staff. The facility may remove or suspend access for unsafe behavior, abusive conduct, or repeated refusal to follow facility rules.`;

interface WaiverPanelProps {
  orgId: string;
  facility: FacilityDetail | null;
}

export function WaiverPanel(props: WaiverPanelProps) {
  // Draft, initialization and mutation presentation belong to one facility.
  // A completed request for the previous facility must not edit the new one.
  return <WaiverEditor key={JSON.stringify([props.orgId, props.facility?.facility_id])} {...props} />;
}

function WaiverEditor({ orgId, facility }: WaiverPanelProps) {
  const queryClient = useQueryClient();
  const facilityId = facility?.facility_id ?? null;

  // The current published version (if any). Disabled until a facility exists, so
  // the read is never issued for a not-yet-created facility.
  const { data: waiver, isLoading, isSuccess, isError, refetch } = useQuery({
    queryKey: ["facility-waiver", facilityId],
    queryFn: () => getFacilityWaiver(facilityId as string),
    enabled: Boolean(facilityId),
  });

  const [body, setBody] = useState(DEFAULT_PLAYER_WAIVER);
  const [initialized, setInitialized] = useState(false);

  // Seed the textarea once the waiver query resolves: existing content if a
  // version is published, otherwise the default template.
  useEffect(() => {
    if (!facilityId || !isSuccess || initialized) return;
    setBody(waiver?.content ?? DEFAULT_PLAYER_WAIVER);
    setInitialized(true);
  }, [facilityId, initialized, isSuccess, waiver]);

  const publishMutation = useMutation({
    mutationFn: () => publishFacilityWaiver(facilityId as string, body.trim()),
    onMutate: () => queryClient.cancelQueries({ queryKey: ["facility-waiver", facilityId] }),
    onSuccess: (published) => {
      // The publish response is authoritative. Never re-seed from the stale
      // pre-publish cache while invalidation's fresh read is still in flight.
      queryClient.setQueryData(["facility-waiver", facilityId], published);
      setBody(published.content);
      setInitialized(true);
      void queryClient.invalidateQueries({
        queryKey: ["facility-waiver", facilityId],
      });
      // The "Publish player waiver" step is server-authoritative and read
      // through the onboarding-state query, so re-resolve it (mirrors
      // StripeConnectPanel) — otherwise the checklist keeps its "Needs action"
      // pill until a manual refetch.
      void queryClient.invalidateQueries({
        queryKey: ["org", orgId, "onboarding-state"],
      });
    },
  });

  // The waiver read is in flight (only possible with a facility) — show a spinner.
  if (facilityId && isLoading) {
    return (
      <Stack sx={{ alignItems: "center", py: 4 }}>
        <CircularProgress aria-label="Loading waiver" size={24} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {!facilityId ? (
        <FormMessage tone="error">
          Create the facility identity before publishing a waiver.
        </FormMessage>
      ) : isError ? (
        <FormMessage tone="error">
          Could not load the current waiver. Your draft has been kept.
          <Button size="small" onClick={() => void refetch()}>Retry loading waiver</Button>
        </FormMessage>
      ) : waiver ? (
        <Paper
          component={Stack}
          direction="row"
          spacing={1}
          variant="outlined"
          sx={{ alignItems: "center", flexWrap: "wrap", px: 1.5, py: 1 }}
        >
          <Chip
            color="success"
            label="Published"
            size="small"
            variant="outlined"
          />
          <Typography color="text.secondary" variant="body2">
            Version {waiver.version_number} published on{" "}
            {new Date(waiver.published_at).toLocaleDateString("en-US")}.
          </Typography>
        </Paper>
      ) : (
        <Typography color="text.secondary" variant="body2">
          No version has been published yet. Review the default waiver text
          below, then publish it.
        </Typography>
      )}
      <TextField
        disabled={!facilityId || !isSuccess || !initialized || publishMutation.isPending}
        fullWidth
        label="Player Waiver Text"
        multiline
        onChange={(event) => setBody(event.target.value)}
        placeholder="Write the waiver players must accept before joining or booking."
        rows={10}
        value={body}
      />
      {publishMutation.isError ? (
        <FormMessage tone="error">
          Failed to publish waiver. Please try again.
        </FormMessage>
      ) : null}
      <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
        <Button
          disabled={!facilityId || !isSuccess || !initialized || !body.trim() || publishMutation.isPending}
          onClick={() => publishMutation.mutate()}
          size="small"
          variant="contained"
        >
          {publishMutation.isPending
            ? "Publishing..."
            : waiver
              ? "Publish New Version"
              : "Publish Waiver"}
        </Button>
      </Stack>
    </Stack>
  );
}
